import React, { useEffect, useState } from "react";
import useDarkModeStatus from './useDarkModeStatus';
import useSequencerDarkModeStatus from "./useSequencerDarkModeStatus";
import { Segmented, Skeleton, theme} from "antd";

const cssVar = (variableName) => {
  const rootStyles = getComputedStyle(document.documentElement);
  return rootStyles.getPropertyValue(variableName).trim();
};

const useDefaultTrebbleAntDTokenConfig = (params) => {
    const [isSystemPreferenceDarkMode, setIsSystemPreferenceDarkMode] = useState(
        window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    );
    const useEditorTheme = params?.useEditorTheme || false;
    const darkModeStatus = useDarkModeStatus();
    const editorInDarkMode = useSequencerDarkModeStatus();

    useEffect(() => {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        
        const handleChange = (e) => {
            setIsSystemPreferenceDarkMode(e.matches);
        };

        // Add listener for changes
        mediaQuery.addEventListener('change', handleChange);

        // Cleanup
        return () => mediaQuery.removeEventListener('change', handleChange);
    }, []);

    const inDarkMode = useEditorTheme ? isSystemPreferenceDarkMode || darkModeStatus || editorInDarkMode : isSystemPreferenceDarkMode || darkModeStatus;

    return {
        inDarkMode,
        algorithm: inDarkMode? theme.darkAlgorithm: theme.defaultAlgorithm,
        token: {
          algorithm: inDarkMode? theme.darkAlgorithm: theme.defaultAlgorithm,
          fontSize: 15,
          lineHeight:	1.5714285714285714,
          borderRadius: 1,
          borderRadiusXS: 0,
          colorWarning: cssVar('--color-warning-bright'),
          colorError: cssVar('--color-error-bright'),
          colorPrimary: cssVar('--trebble-primary'),
          colorInfo: cssVar('--trebble-primary'),
          colorSplit: inDarkMode? cssVar('--color-white-10'):cssVar('--color-black-10'),
          colorTextBase: inDarkMode ? cssVar('--color-white') : cssVar('--color-black'),
          colorTextPlaceholder: inDarkMode ? cssVar('--color-white-50') : cssVar('--color-black-50'),
          colorText: inDarkMode ? cssVar('--color-white-80') : cssVar('--color-black-80'),
          colorTextDescription: inDarkMode ? cssVar('--color-white-50') : cssVar('--color-black-50'),
          fontFamily: `${cssVar('--trebble-font')},sans-serif`,
          colorBgElevated: inDarkMode ? cssVar('--color-black-100') : cssVar('--color-gray-100'),
          controlItemBgActive: inDarkMode ? cssVar('--color-white-20') : cssVar('--color-black-5'),
          controlItemBgHover: inDarkMode? cssVar('--color-white-10'):cssVar('--color-black-5'),
          controlItemBgActiveHover: inDarkMode? cssVar('--color-white-10'):cssVar('--color-black-5'),
          colorLink: cssVar('--trebble-primary'),
          colorLinkActive: cssVar('--trebble-primary'),
          colorLinkHover: cssVar('--trebble-primary-600'),
          colorPrimaryActive : cssVar('--trebble-primary'),
        },
        components: {
          Table: {
            fontFamily: '"var(--trebble-font)",sans-serif',
            headerBorderRadius: 0,
            fontWeightStrong: 600,
            colorLink: cssVar('--trebble-primary'),
            colorLinkActive: cssVar('--trebble-primary'),
            colorLinkHover: cssVar('--trebble-primary-600'),
            colorPrimary: cssVar('--trebble-primary-600'),
            controlItemBgActive: cssVar('--trebble-primary-100'),
            controlItemBgActiveHover: cssVar('--trebble-primary-100'),
            rowSelectedHoverBg: cssVar('--trebble-primary-100'),
            rowSelectedBg: cssVar('--trebble-primary-100'),
            borderRadius: 0,
            borderRadiusLG: 0
          },
          Segmented: {
            // In light mode keep the legacy white "thumb" so the selected item
            // visibly lifts off the gray track. In dark mode a white thumb
            // collides with the also-white label text, producing an
            // unreadable blank rectangle — use the editor surface tone
            // instead so the label stays readable.
            itemSelectedBg: inDarkMode ? cssVar('--color-white-20') : 'white',
            itemSelectedColor: inDarkMode ? cssVar('--color-white') : cssVar('--color-black-80'),
            // Unselected items: ensure proper contrast in BOTH modes. Default
            // Ant tokens leave this too pale against the track background.
            itemColor: inDarkMode ? cssVar('--color-white-70') : cssVar('--color-black-70'),
            itemHoverColor: inDarkMode ? cssVar('--color-white') : cssVar('--color-black-80'),
            itemActiveBg: inDarkMode ? cssVar('--color-white-10') : cssVar('--color-black-5'),
            trackBg: inDarkMode ? cssVar('--color-white-10') : cssVar('--color-black-5'),
          },
          Skeleton: {
            gradientFromColor: inDarkMode? cssVar('--color-white-5'):cssVar('--color-black-5'),
            gradientToColor: inDarkMode? cssVar('--color-white-20'):cssVar('--color-black-20'),
          },
          Steps: {
            colorPrimary: cssVar('--trebble-primary'),
            borderRadius: 0,
            borderRadiusXS: 0,
            colorText: inDarkMode ? cssVar('--color-white-80') : cssVar('--color-black-80'),
            colorTextDescription: inDarkMode ? cssVar('--color-white-50') : cssVar('--color-black-50'),
            fontWeightStrong: 700,
            titleFontSize: 16,
          },
          Collapse: {
            borderRadius: 0,
            borderRadiusLG: 0,
            colorBorder: inDarkMode ? cssVar('--color-white-20') : cssVar('--color-black-20'),
            colorBgContainer: inDarkMode ? cssVar('--color-black-100') : cssVar('--color-white'),
            colorText: inDarkMode ? cssVar('--color-white-80') : cssVar('--color-black-80'),
          },
          Typography: {
            colorText: inDarkMode ? cssVar('--color-white-80') : cssVar('--color-black-80'),
            colorTextHeading: inDarkMode ? cssVar('--color-white') : cssVar('--color-black'),
            colorTextDescription: inDarkMode ? cssVar('--color-white-50') : cssVar('--color-black-50'),
            fontFamily: `${cssVar('--trebble-font')},sans-serif`,
          },
          Badge: {
            colorPrimary: cssVar('--trebble-primary'),
            colorText: inDarkMode ? cssVar('--color-white') : cssVar('--color-black'),
          },
          Checkbox: {
            colorPrimary: cssVar('--trebble-primary'),
            colorText: inDarkMode ? cssVar('--color-white-80') : cssVar('--color-black-80'),
            borderRadius: 0,
          },
          Card: {
            borderRadius: 0,
            colorBorderSecondary: inDarkMode ? cssVar('--color-white-20') : cssVar('--color-black-20'),
            colorBgContainer: inDarkMode ? cssVar('--color-black-100') : cssVar('--color-white'),
          },
          Select: {
            optionSelectedBg: inDarkMode ? cssVar('--color-white-20') : cssVar('--trebble-primary-15'),
            optionSelectedColor: inDarkMode ? cssVar('--color-white') : cssVar('--text-dark'),
          }

        },
      }
}

export default useDefaultTrebbleAntDTokenConfig;